import log from "loglevel";

// How long to keep the throwaway socket open waiting for the dispatcher's
// "Warming" reply. The reply is only a courtesy that lets us close early:
// API Gateway has already delivered the message, and the warm-up happens
// server-side regardless, so a timeout here is not a failure.
const WARM_REPLY_TIMEOUT_MS = 5_000;

// Pre-warms the support-chat Lambdas before the visitor has typed anything.
//
// A cold first message pays two Lambda cold starts in series (dispatcher, then
// worker) -- about 5 s even after the backend's Init-time import change (cca
// documentation/43-support-chat.md, "Cold-start latency"). A visitor almost
// always spends longer than that between a page loading and their first
// question, so sending {"action_handler": "warm"} now means the containers are
// already up when the question arrives.
//
// Uses its own short-lived WebSocket rather than the provider's, on purpose:
// the provider's socket connects only while the panel is open, and keeping it
// open for every visitor's whole tab session would let API Gateway's 10-minute
// idle timeout exhaust the reconnect budget and leave the panel with a dead
// socket. A throwaway socket sends one message, closes on the reply (or after
// a timeout), and never interacts with the chat's lifecycle.
//
// Best-effort by design: a socket that cannot be created, errors, or never
// answers is logged at debug and dropped. A warm-up that fails costs the
// visitor nothing but the cold start they would have had anyway.
//
// It warms one container per call. Concurrent visitors beyond the first still
// hit cold ones, and Lambda reclaims an idle container after some minutes. The
// provider calls this on intent -- hover or focus on the bubble, and the panel
// opening -- behind a short cooldown, never on page load: each call starts a
// backend container, and only about 1 in 250 sessions ever uses the chat
// (SupportChatProvider, prewarm).
// `globalThis` rather than `window` so the default works under Jest's node environment
// too; Gatsby's develop-time ESLint config predates ES2020 globals and reports it as
// undefined, which fails the development bundle outright.
/* global globalThis */
export function warmSupportChat(url, WebSocketImpl = globalThis.WebSocket) {
  if (!url || typeof WebSocketImpl !== "function") {
    return;
  }
  let socket;
  try {
    socket = new WebSocketImpl(url);
  } catch (error) {
    log.debug("support-chat: warm-up socket could not be created", error);
    return;
  }
  const finish = () => {
    clearTimeout(timer);
    if (
      socket.readyState === WebSocketImpl.OPEN ||
      socket.readyState === WebSocketImpl.CONNECTING
    ) {
      socket.close();
    }
  };
  const timer = setTimeout(finish, WARM_REPLY_TIMEOUT_MS);
  socket.onopen = () => {
    socket.send(JSON.stringify({ action_handler: "warm" }));
  };
  socket.onmessage = finish;
  // A server that closes without replying must not leave the timer pending.
  socket.onclose = finish;
  socket.onerror = (event) => {
    log.debug("support-chat: warm-up socket error", event);
    finish();
  };
}
